import React from 'react';
import { useTranslation } from 'react-i18next';
import { EvaluationValueBadge } from '@/components/EvaluationValueBadge';
import type { EvaluationEdit } from '@/types/overview';
import { formatDateTime } from '@/utils/date';

const ROW_CLASS =
  'grid grid-cols-[148px_minmax(0,1fr)_200px] border-b border-border-table last:border-b-0';

const CELL_CLASS =
  'flex h-12 items-center border-r border-border-table pr-3 pl-5 last:border-r-0';

interface Props {
  lessonTypeId: string;
  edits: EvaluationEdit[];
}

export const EvaluationHistoryTable: React.FC<Props> = ({
  lessonTypeId,
  edits,
}) => {
  const { t } = useTranslation();

  return (
    <div className="text-body-14 overflow-hidden rounded-2xl bg-card text-foreground shadow-card contrast:border contrast:border-border">
      <div className="md:hidden">
        <div className="text-subhead-14 flex justify-between gap-3 border-b border-border-table bg-on-primary-surface p-4">
          <span>{t('diary.evaluation.historyValue')}</span>
          <span>{t('diary.evaluation.historyAuthorAndDate')}</span>
        </div>

        {edits.map((edit, index) => (
          <div
            key={index}
            className="flex items-center justify-between gap-3 border-b border-border-table p-4 last:border-b-0"
          >
            <EvaluationValueBadge
              value={edit.value}
              valueFullName={edit.valueFullName}
              lessonTypeId={lessonTypeId}
              nonAttendanceType={edit.nonAttendanceType}
            />
            <span className="flex min-w-0 flex-col items-end gap-0.5 text-right">
              <span className="max-w-full truncate">{edit.authorName}</span>
              <span>{formatDateTime(edit.timeCreated)}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="hidden md:block">
        <div className={`bg-on-primary-surface ${ROW_CLASS}`}>
          <span className={CELL_CLASS}>
            {t('diary.evaluation.historyValue')}
          </span>
          <span className={CELL_CLASS}>
            {t('diary.evaluation.historyAuthor')}
          </span>
          <span className={CELL_CLASS}>
            {t('diary.evaluation.historyDate')}
          </span>
        </div>

        {edits.map((edit, index) => (
          <div key={index} className={ROW_CLASS}>
            <span className={CELL_CLASS}>
              <EvaluationValueBadge
                value={edit.value}
                valueFullName={edit.valueFullName}
                lessonTypeId={lessonTypeId}
                nonAttendanceType={edit.nonAttendanceType}
              />
            </span>
            <span className={`${CELL_CLASS} min-w-0`}>
              <span className="truncate">{edit.authorName}</span>
            </span>
            <span className={CELL_CLASS}>
              {formatDateTime(edit.timeCreated)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
